import { AnimatePresence } from 'motion/react';
import { memo, useCallback, useMemo } from 'react';

import { useTrackAgentPanelOpened, type WorkspaceAgentThreadCreatedLocation } from '@air/analytics';
import { Portal } from '@air/primitive-portal';
import { cn } from '@air/utils-tailwind';

import { PrivateAgentSelectionBar } from '~/components/v2/components/private-selection-bar/agent-selection-bar/PrivateAgentSelectionBar';
import { useStrandedCanvasSelectionCleanup } from '~/components/v2/components/private-selection-bar/hooks/useStrandedCanvasSelectionCleanup';
import { WorkspaceAgentIdleFab } from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/WorkspaceAgentIdleFab';
import { WorkspaceAgentSelectionBarStartTaskButton } from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/WorkspaceAgentSelectionBarStartTaskButton';
import { WorkspaceAgentSpaceShell } from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/WorkspaceAgentSpaceShell';
import { useIsWorkspaceAgentPanelAvailable } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useIsWorkspaceAgentPanelAvailable';
import { useWorkspaceAgentCreditsDepleted } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentCreditsDepleted';
import { useWorkspaceAgentIdleFab } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentIdleFab';
import { useWorkspaceAgentSpacePhase } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentSpacePhase';
import { useWorkspaceSelectionBarZIndexClass } from '~/components/v2/hooks/useWorkspaceSelectionBarZIndexClass';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import { useWorkspaceSelectionBarStore } from '~/components/v2/store/features/workspace/useWorkspaceSelectionBarStore';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

export type PrivateWorkspaceAgentSpaceProps = {
  /** The canvas editor brings its own agent chat, so the selection bar stands down while it is open. */
  canvasOpen: boolean;
};

/**
 * The workspace selection bar affordance. Slides up when something is selected and slides back down
 * when the selection clears. Agent entry without a selection is the header Start task split on
 * ui2026 (WORK-461), or the idle FAB while that chrome is off.
 *
 * Mounted once by the layout, so it outlives page navigation. That is also why the canvas-only
 * selection cleanup below watches the pathname rather than its own unmount.
 */
export const PrivateWorkspaceAgentSpace = memo(({ canvasOpen }: PrivateWorkspaceAgentSpaceProps) => {
  const { ui2026 } = useTemporaryFeatureFlags();
  const { isAgentSelectionBarEnabled, canShowAgentStartTask } = useIsWorkspaceAgentPanelAvailable();
  const portal = useWorkspaceSelectionBarStore((state) => state.portal);
  const { selectionBarZIndexClass } = useWorkspaceSelectionBarZIndexClass();
  const dock = useWorkspaceAgentPanelStore((state) => state.dock);
  const startNewThread = useWorkspaceAgentPanelStore((state) => state.startNewThread);
  const showChat = useWorkspaceAgentPanelStore((state) => state.showChat);
  const { blockIfCreditsDepleted } = useWorkspaceAgentCreditsDepleted();
  const { trackAgentPanelOpened } = useTrackAgentPanelOpened();

  const startNewThreadWhenCreditsAllow = useCallback(
    (location: WorkspaceAgentThreadCreatedLocation) => {
      if (blockIfCreditsDepleted(() => showChat({ focusComposer: false }))) {
        return;
      }

      startNewThread(location);
    },
    [blockIfCreditsDepleted, showChat, startNewThread],
  );
  const idleFab = useWorkspaceAgentIdleFab();

  const { phase, isBarDrawn, isCollapsing, onShellSettled, onExitComplete } = useWorkspaceAgentSpacePhase({
    canvasOpen,
  });

  const handleStartNewThreadFromToolbar = useCallback(() => {
    const wasMinimized = dock === 'minimized';
    startNewThreadWhenCreditsAllow('toolbar');

    if (wasMinimized) {
      trackAgentPanelOpened({ location: 'toolbar' });
    }
  }, [dock, startNewThreadWhenCreditsAllow, trackAgentPanelOpened]);

  const handleStartNewThreadFromFab = useCallback(() => {
    const wasMinimized = dock === 'minimized';
    startNewThreadWhenCreditsAllow('fab');

    if (wasMinimized) {
      trackAgentPanelOpened({ location: 'fab' });
    }
  }, [dock, startNewThreadWhenCreditsAllow, trackAgentPanelOpened]);

  const endCta = useMemo(
    () =>
      canShowAgentStartTask ? (
        <WorkspaceAgentSelectionBarStartTaskButton onClick={handleStartNewThreadFromToolbar} />
      ) : undefined,
    [canShowAgentStartTask, handleStartNewThreadFromToolbar],
  );

  useStrandedCanvasSelectionCleanup({ watchPathname: true });

  if (!isAgentSelectionBarEnabled || !portal) {
    return null;
  }

  let spaceChrome = null;

  if (isBarDrawn) {
    spaceChrome = (
      <WorkspaceAgentSpaceShell
        barContent={<PrivateAgentSelectionBar endCta={endCta} holdViewModelWhileCollapsed={isCollapsing} />}
        isCollapsing={isCollapsing}
        isShown={phase === 'unified'}
        key="workspace-agent-space-bar"
        onShellSettled={onShellSettled}
        reservesStatusBarStrip={!ui2026}
      />
    );
  } else if (phase === 'fab') {
    spaceChrome = (
      <WorkspaceAgentIdleFab
        buttonRef={idleFab.buttonRef}
        ctaTransitionStyle={idleFab.ctaTransitionStyle}
        isExpanded={idleFab.isExpanded}
        key="workspace-agent-space-fab"
        onBlur={idleFab.handleBlur}
        onFocus={idleFab.handleFocus}
        onOpen={handleStartNewThreadFromFab}
        onPointerEnter={idleFab.handlePointerEnter}
        onPointerLeave={idleFab.handlePointerLeave}
      />
    );
  }

  return (
    <Portal asChild container={portal}>
      <div
        className={cn(
          'pointer-events-none absolute right-2 bottom-10 ui2026:bottom-2 left-2 m-2 flex flex-col items-end',
          selectionBarZIndexClass,
        )}
      >
        <AnimatePresence mode="wait" onExitComplete={onExitComplete}>
          {spaceChrome}
        </AnimatePresence>
      </div>
    </Portal>
  );
});

PrivateWorkspaceAgentSpace.displayName = 'PrivateWorkspaceAgentSpace';
